import { ElementRef, Injectable, OnDestroy } from "@angular/core";

/**
 * Utility class for instantiating AngularJS components from modern Angular components.
 * This provides a bridge between AngularJS and modern Angular applications.
 */
export class AngularJSComponentUtil {
    private activeComponents: Map<string, any> = new Map();

    /**
     * Converts camelCase string to kebab-case for HTML attributes
     * @param str - The camelCase string to convert
     * @returns The kebab-case string
     */
    private camelToKebab(str: string): string {
        return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
    }

    /**
     * Creates and instantiates an AngularJS component within a modern Angular component.
     *
     * @param componentName - The name of the AngularJS component to instantiate
     * @param containerElement - The Angular ElementRef where the component should be rendered
     * @param bindings - The bindings/inputs to pass to the AngularJS component
     * @param scope - Optional parent scope for the AngularJS component
     * @returns Promise that resolves when the component is initialized
     */
    public async createAngularJSComponent(
        componentName: string,
        containerElement: ElementRef,
        bindings: any = {},
        scope?: any,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                // Ensure angBridge is available
                if (!window.angBridge) {
                    throw new Error("angBridge is not available. Make sure AngularJS bridge is initialized.");
                }

                const { $compile, $rootScope, $q, element } = window.angBridge;

                // Create a new scope for the component
                const componentScope = scope || $rootScope.$new();

                // Build component element with bindings as attributes
                let componentHtml = `<${componentName}`;
                Object.keys(bindings).forEach((key) => {
                    const value = bindings[key];
                    const kebabKey = this.camelToKebab(key);

                    if (typeof value === "string") {
                        componentHtml += ` ${kebabKey}="${value}"`;
                    } else if (typeof value === "object" && value !== null) {
                        // For objects/functions, we need to bind them to the scope and reference them
                        componentScope[key] = value;
                        componentHtml += ` ${kebabKey}="${key}"`;
                    } else {
                        componentHtml += ` ${kebabKey}="${value}"`;
                    }
                });
                componentHtml += `></${componentName}>`;

                // Create the component element
                const componentElement = element(componentHtml);

                // Compile the component
                const compiledElement = $compile(componentElement)(componentScope);

                // Append to container
                element(containerElement.nativeElement).append(compiledElement);

                // Trigger digest cycle
                componentScope.$digest();

                // Store reference for cleanup
                const componentId = this.generateComponentId();
                this.activeComponents.set(componentId, {
                    scope: componentScope,
                    element: compiledElement,
                    componentName,
                });

                // Wait for component initialization
                $q.when().then(() => {
                    resolve({
                        componentId,
                        scope: componentScope,
                        element: compiledElement,
                        destroy: () => this.destroyComponent(componentId),
                    });
                });
            } catch (error) {
                console.error(`Failed to create AngularJS component ${componentName}:`, error);
                reject(error);
            }
        });
    }

    /**
     * Destroys an AngularJS component and cleans up its resources.
     *
     * @param componentId - The ID of the component to destroy
     */
    public destroyComponent(componentId: string): void {
        const component = this.activeComponents.get(componentId);
        if (component) {
            try {
                // Destroy the scope
                component.scope.$destroy();

                // Remove the element
                component.element.remove();

                // Remove from active components
                this.activeComponents.delete(componentId);
            } catch (error) {
                console.error(`Error destroying AngularJS component ${component.componentName}:`, error);
            }
        }
    }

    /**
     * Destroys all active AngularJS components.
     */
    public destroyAllComponents(): void {
        this.activeComponents.forEach((component, componentId) => {
            this.destroyComponent(componentId);
        });
    }

    /**
     * Generates a unique component ID.
     */
    private generateComponentId(): string {
        return `angjs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Creates a deferred promise for AngularJS components that need to resolve after initialization.
     *
     * @param componentName - The name of the component
     * @param containerElement - The container element
     * @param bindings - The bindings object
     * @param deferProperty - The property name on bindings that contains the defer promise
     * @returns Promise that resolves when the component's defer is resolved
     */
    public async createAngularJSComponentWithDefer(
        componentName: string,
        containerElement: ElementRef,
        bindings: any,
        deferProperty: string = "defer",
    ): Promise<any> {
        const { $q } = window.angBridge;

        // Create a defer for this component
        const componentDefer = $q.defer();

        // Add the defer to bindings
        const bindingsWithDefer = {
            ...bindings,
            [deferProperty]: componentDefer,
        };

        // Create the component
        const componentInstance = await this.createAngularJSComponent(
            componentName,
            containerElement,
            bindingsWithDefer,
        );

        // Return a promise that resolves when the component's defer resolves
        return componentDefer.promise.then((result: any) => {
            return {
                ...componentInstance,
                result,
            };
        });
    }
}
